import { useState } from 'react';
import { ConditionsNew } from '@retail/calculus/types';
import {
  AddButton,
  CalculusActionPortal,
  SuspenseWithSentryErrorBoundary,
} from '@retail/components';
import { useLanguageSelector } from '@retail/app/hooks';
import { CircularProgress, Stack, Typography } from '@mui/material';
import { useDisclosure } from '@shared/hooks';
import { usePermissionsForCurrentUser } from '@retail/app/stores/selected-context';
import { OutlinedButton } from '@shared/components';
import { ExportMenu } from '@retail/calculus/components';
import { useAppTFunction } from '@retail/app/i18n';
import { FreightConditionsTable } from '@retail/freight/components';
import { useFreightExport } from './UploadFreightConditions/useFreightExport';
import { FreightConditionActions } from './FreightConditionsActions';
import { AffectedItemsDialog, ItemsAffected } from '../AffectedItems';

interface Props {
  conditions: ConditionsNew[];
  isFetching: boolean;
  supplierNumber?: number;
}
export const FreightConditions = ({ conditions, isFetching, supplierNumber }: Props) => {
  const t = useAppTFunction();
  const { language } = useLanguageSelector();

  const { exportAsCsv, exportAsXLSX } = useFreightExport();

  const { userCanCreate, userCanUpdate } = usePermissionsForCurrentUser();
  const userCanCreateFreightConditions = userCanCreate('PRICE_CALCULATION__FREIGHT');
  const userCanUpdateFreightConditions = userCanUpdate('PRICE_CALCULATION__FREIGHT');

  const { isOpen: uploadConditions, onToggle: toggleUploadDialog } = useDisclosure();
  const { isOpen: createCondition, onToggle: toggleCreate } = useDisclosure();

  const [editCondition, setEdit] = useState<ConditionsNew | undefined>();
  const [deleteConditionId, setDelete] = useState<number | undefined>();
  const [deactivateConditionId, setDeactivate] = useState<number | undefined>();

  const [conditionForAffectedItems, setConditionForAffectedItems] = useState<
    ConditionsNew | undefined
  >();

  const toggleCreateDialog = userCanCreateFreightConditions ? toggleCreate : undefined;
  const conditionActions =
    userCanUpdateFreightConditions && supplierNumber
      ? { setDeactivate, setEdit, setDelete }
      : undefined;

  return (
    <Stack flexGrow={1}>
      <CalculusActionPortal>
        <ExportMenu
          exportDataAsCsv={() => exportAsCsv(conditions)}
          exportDataAsExcel={() => exportAsXLSX(conditions)}
        />
        <OutlinedButton
          color="secondary"
          size="small"
          onClick={toggleUploadDialog}
          disabled={!userCanCreateFreightConditions || !supplierNumber}
        >
          {t('calculus.actions.uploadFreight')}
        </OutlinedButton>

        <AddButton
          onClick={toggleCreateDialog}
          disabled={!userCanCreateFreightConditions || !supplierNumber}
        >
          {t('calculus.createFreightCondition')}
        </AddButton>
      </CalculusActionPortal>

      <FreightConditionsTable
        loading={isFetching}
        language={language}
        conditions={conditions}
        conditionActions={conditionActions}
        openCreateDialog={toggleCreateDialog}
        renderAffectedItemsCell={({ row }) => {
          if (row.bindingType === 'MG_SUPPLIER_ITEM') return '-';
          return (
            <SuspenseWithSentryErrorBoundary
              suspenseFallback={<CircularProgress size={18} />}
              errorFallback={() => (
                <Typography variant="body3">{t('calculus.error.generic')}</Typography>
              )}
            >
              {row.supplier ? (
                <ItemsAffected
                  supplierNumber={row.supplier.supplierNumber}
                  conditionIds={[row.freightCondition!.conditionId]}
                  openItemsAffectedModal={() => setConditionForAffectedItems(row)}
                />
              ) : (
                '-'
              )}
            </SuspenseWithSentryErrorBoundary>
          );
        }}
      />

      <FreightConditionActions
        supplierNumber={supplierNumber}
        createCondition={createCondition}
        uploadConditions={uploadConditions}
        editCondition={editCondition}
        deactivateConditionId={deactivateConditionId}
        deleteConditionId={deleteConditionId}
        closeCreate={toggleCreate}
        closeUpload={toggleUploadDialog}
        closeEdit={() => setEdit(undefined)}
        closeDeactivate={() => setDeactivate(undefined)}
        closeDelete={() => setDelete(undefined)}
      />

      {conditionForAffectedItems && (
        <AffectedItemsDialog
          open
          onClose={() => setConditionForAffectedItems(undefined)}
          conditionIds={[conditionForAffectedItems.id]}
          supplierNumber={conditionForAffectedItems.supplier!.supplierNumber}
        />
      )}
    </Stack>
  );
};
